import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, Calendar, CreditCard, Package } from 'lucide-react';
import axios from 'axios';
import { useCurrentAdmin } from '../../lib/useCurrentAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'processing', label: 'Processing', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'shipped', label: 'Shipped', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200' },
];

const stageMeta = (stage) => STAGES.find((s) => s.value === stage) || STAGES[0];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const { isAdmin } = useCurrentAdmin();

  useEffect(() => {
    fetchOrders();
  }, []);

  const token = localStorage.getItem('admin_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/orders`, { headers: authHeaders });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateStage = async (orderId, newStage) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/orders/${orderId}/stage`,
        { stage: newStage },
        { headers: authHeaders }
      );
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, stage: newStage } : o)));
      toast.success(`Stage updated to ${newStage}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    }
  };

  const counts = useMemo(() => {
    const c = { all: orders.length };
    for (const s of STAGES) c[s.value] = 0;
    for (const o of orders) c[o.stage || 'new'] = (c[o.stage || 'new'] || 0) + 1;
    return c;
  }, [orders]);

  const filteredOrders = stageFilter === 'all' ? orders : orders.filter((o) => (o.stage || 'new') === stageFilter);
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [stageFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'expired': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold">Orders & Transactions</h1>
            <p className="text-gray-600 mt-2">{orders.length} total orders</p>
          </div>
        </div>

        {/* Stage filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="orders-stage-filter">
          <Button
            variant={stageFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStageFilter('all')}
            className={stageFilter === 'all' ? 'text-white' : ''}
            style={stageFilter === 'all' ? { backgroundColor: '#00CED1' } : { borderColor: '#00CED1', color: '#00CED1' }}
            data-testid="orders-stage-filter-all"
          >
            All <Badge variant="secondary" className="ml-2">{counts.all}</Badge>
          </Button>
          {STAGES.map((s) => (
            <Button
              key={s.value}
              variant={stageFilter === s.value ? 'default' : 'outline'}
              onClick={() => setStageFilter(s.value)}
              className={stageFilter === s.value ? 'text-white' : ''}
              style={stageFilter === s.value ? { backgroundColor: '#00CED1' } : { borderColor: '#00CED1', color: '#00CED1' }}
              data-testid={`orders-stage-filter-${s.value}`}
            >
              {s.label}
              <Badge variant="secondary" className="ml-2">{counts[s.value] || 0}</Badge>
            </Button>
          ))}
        </div>

        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">
                {stageFilter === 'all'
                  ? 'No orders yet.'
                  : `No orders in "${stageMeta(stageFilter).label}" stage.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pagedOrders.map((order) => {
              const currentStage = order.stage || 'new';
              const meta = stageMeta(currentStage);
              return (
                <Card key={order.id} className="hover:shadow-lg transition-shadow" data-testid={`order-row-${order.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4 gap-4 flex-wrap">
                      <div className="min-w-0 flex-grow">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                          <h3 className="text-lg font-bold">Order #{order.session_id?.slice(-8) || order.id?.slice(-8)}</h3>
                          <Badge className={getPaymentStatusColor(order.payment_status)}>
                            {order.payment_status}
                          </Badge>
                          <Badge variant="outline" className={`capitalize ${meta.color}`}>
                            <Package className="h-3 w-3 mr-1" />
                            {meta.label}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" style={{ color: '#00CED1' }} />
                            {formatDate(order.createdAt)}
                          </span>
                          <span className="flex items-center">
                            <CreditCard className="h-4 w-4 mr-1" style={{ color: '#00CED1' }} />
                            {(order.currency || 'zar').toUpperCase()}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <p className="text-2xl font-bold" style={{ color: '#00CED1' }}>
                          R{(order.amount || 0).toFixed(2)}
                        </p>

                        {/* Stage selector — admin-only; editors see read-only badge */}
                        {isAdmin ? (
                          <Select value={currentStage} onValueChange={(v) => updateStage(order.id, v)}>
                            <SelectTrigger className="w-44 h-9" data-testid={`order-stage-select-${order.id}`}>
                              <SelectValue placeholder="Set stage" />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGES.map((s) => (
                                <SelectItem key={s.value} value={s.value} data-testid={`order-stage-option-${s.value}`}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge
                            variant="outline"
                            className={`capitalize ${stageMeta(currentStage).color}`}
                            data-testid={`order-stage-readonly-${order.id}`}
                          >
                            {stageMeta(currentStage).label}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {order.items && order.items.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2 text-sm text-gray-700">Order Items:</h4>
                        <div className="space-y-2">
                          {order.items.map((item, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center p-3 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-3">
                                {item.image && (
                                  <img
                                    src={item.image.startsWith('http') ? item.image : `${BACKEND_URL}${item.image}`}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded"
                                  />
                                )}
                                <div>
                                  <p className="font-medium">{item.name}</p>
                                  <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                                </div>
                              </div>
                              <p className="font-semibold">R{(item.price * item.quantity).toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t flex justify-between items-center text-xs text-gray-500">
                      <span>Session: {order.session_id || order.id}</span>
                      {order.stageUpdatedAt && (
                        <span>Stage updated: {formatDate(order.stageUpdatedAt)}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {filteredOrders.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-6" data-testid="admin-orders-pagination">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ borderColor: '#00CED1', color: '#00CED1' }}
                data-testid="admin-orders-prev-page"
              >
                Previous
              </Button>
              <span className="px-3 py-2 text-sm font-medium">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{ borderColor: '#00CED1', color: '#00CED1' }}
                data-testid="admin-orders-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
